import { Box, Link as ChakraLink, Flex, Text } from "@chakra-ui/react";
import { LayoutGroup } from "framer-motion";
import { useCallback, useEffect, useState, VFC } from "react";

import { track } from "lib/analytics";
import { useScrollFade } from "lib/use-scroll-fade";
import { Animation } from "ui";

export type TableOfContentsItem = {
  value: string;
  depth: number;
  id: string;
};

type Props = {
  toc: TableOfContentsItem[];
};

function useActiveHeading(ids: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);
  const key = ids.join("|");

  useEffect(() => {
    const slugs = key ? key.split("|") : [];
    if (slugs.length === 0) return;

    // Offset the active line by the shared header height (+ breathing room).
    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--theme-sizes-header",
        ),
        10,
      ) || 64;
    const activeOffset = headerHeight + 40;

    const onScroll = () => {
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;
      if (atBottom) {
        setActiveId(slugs[slugs.length - 1]);
        return;
      }
      // The active heading is the last one scrolled past the offset line.
      let current = slugs[0];
      for (const id of slugs) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - activeOffset <= 0) current = id;
        else break;
      }
      setActiveId(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [key]);

  return activeId;
}

export const TableOfContents: VFC<Readonly<Props>> = ({ toc }) => {
  const activeId = useActiveHeading((toc ?? []).map((item) => item.id));

  const handleTocClick = useCallback((heading: TableOfContentsItem) => {
    track("TOC Link Clicked", {
      page_path: window.location.pathname,
      heading_text: heading.value,
      heading_id: heading.id,
      heading_depth: heading.depth,
    });
  }, []);

  const { ref: listRef, maskImage } = useScrollFade([toc]);

  if (!toc?.length) {
    return null;
  }

  const minDepth = Math.min(...toc.map((item) => item.depth));

  return (
    <Flex
      as="nav"
      aria-label="On this page"
      direction="column"
      flexGrow={0}
      flexShrink={1}
      minH={0}
      borderTop="1px"
      borderColor="base.divider"
      pt={5}
    >
      <Text
        as="span"
        textStyle="mono"
        fontSize="xs"
        fontWeight="semibold"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color="text.tertiary"
        mb={2.5}
      >
        On this page
      </Text>

      <Box
        ref={listRef}
        as="ul"
        listStyleType="none"
        display="flex"
        flexDirection="column"
        gap={0.5}
        overflowY="auto"
        flex="1"
        minH={0}
        mx={-2}
        px={2}
        py={1}
        sx={{
          maskImage,
          WebkitMaskImage: maskImage,
          scrollPaddingBlock: "8px",
          "&:focus-within": { maskImage: "none", WebkitMaskImage: "none" },
        }}
      >
        <LayoutGroup id="toc">
          {toc.map(({ value, id, depth }) => {
            const isActive = id === activeId;
            const level = depth - minDepth;
            return (
              <Box as="li" key={id}>
                <ChakraLink
                  href={`#${id}`}
                  position="relative"
                  display="block"
                  py={0.5}
                  borderRadius="none"
                  borderRightRadius="base"
                  pl={`${18 + level * 12}px`}
                  pr={2}
                  fontSize={level === 0 ? "md" : "sm"}
                  lineHeight="1.5"
                  textDecoration="none"
                  outline="none"
                  transitionProperty="color"
                  transitionDuration="150ms"
                  color={
                    isActive
                      ? "primary.text"
                      : level === 0
                      ? "text.primary"
                      : "text.tertiary"
                  }
                  fontWeight={level === 0 ? "medium" : "normal"}
                  _hover={{ color: isActive ? "primary.text" : "text.primary" }}
                  _focusVisible={{ boxShadow: "outline" }}
                  onClick={() => handleTocClick({ value, id, depth })}
                >
                  {isActive && (
                    <Animation
                      aria-hidden="true"
                      layoutId="toc-indicator"
                      // @ts-expect-error Framer Motion overrides Chakra's `transition` prop
                      transition={{
                        default: { ease: "easeInOut" },
                        layout: { duration: 0.2 },
                      }}
                      position="absolute"
                      left={0}
                      top={0}
                      bottom={0}
                      width="2px"
                      bg="primary.base"
                      borderRadius="full"
                    />
                  )}
                  {/* Needs a wrapper element to avoid crashing when using Google Translate on the page. */}
                  <Box as="span">{value}</Box>
                </ChakraLink>
              </Box>
            );
          })}
        </LayoutGroup>
      </Box>
    </Flex>
  );
};
